import StarRating from '../../../../../components/StarRating';
import { mapToOption, formatDateCST, formatTimeCST } from '../../../../../utlis';
import { timezoneOptions, formatTimeInput } from '../candidateInterviewUtils';

const CandidateInterviewsForm = ({
  register,
  errors,
  handleSubmit,
  onSubmit,
  closeModal,
  setValue,
  watch,
  isSubmitting,
  isValid,
  refVendor,
  refVendorLoading,
  refTechnologies,
  refTechnologiesLoading,
  jobs,
  selectedJob,
  selectedJobDetails,
  onJobChange,
  salesPersons,
  salesPersonsLoading,
  isReadOnly,
}) => {
  const isJobSelected = !!selectedJobDetails;
  const schedulingStatus = watch('schedulingStatus');
  const disabledClass = isReadOnly ? 'bg-gray-100 cursor-not-allowed pointer-events-none' : '';

  // Handle time input change with auto-formatting
  const handleTimeInputChange = (e) => {
    const formatted = formatTimeInput(e.target.value);
    setValue('interviewTime', formatted);
  };

  const onRatingChange = (rating) => {
    setValue('rating', parseInt(rating));
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      {isReadOnly && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-2 rounded-lg text-sm">
          This interview is in "Selected" status and can only be edited by an Admin or Manager.
        </div>
      )}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="jobId" className="text-text-hint mb-1">
            Job <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('jobId', { required: 'Job is required', valueAsNumber: true })}
            id="jobId"
            className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
            value={selectedJob}
            onChange={onJobChange}
            disabled={isReadOnly}
          >
            <option value="">Select a job</option>
            {jobs.map((job) => (
              <option key={job.job_id} value={job.job_id}>
                {job.job_title}
              </option>
            ))}
          </select>
          {errors.jobId && <p className="text-red-500">{errors.jobId.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="jobTitle" className="text-text-hint mb-1">
            Job Title
          </label>
          <input
            {...register('jobTitle')}
            placeholder="Job Title"
            id="jobTitle"
            type="text"
            readOnly={isJobSelected || isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg ${isJobSelected || isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="technology" className="text-text-hint mb-1">
            Technology{!isJobSelected && <span className="text-danger-500"> *</span>}
          </label>
          {isJobSelected ? (
            <input
              type="text"
              value={selectedJobDetails.technologyName}
              readOnly
              className="outline-none border-2 border-secondary-500 rounded-lg bg-gray-100 cursor-not-allowed"
            />
          ) : (
            <select
              {...register('technology', { required: 'Technology is required' })}
              className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
              disabled={isReadOnly}
            >
              {!refTechnologiesLoading &&
                mapToOption(refTechnologies?.data?.records, 'technology_name', 'technology_id').map(
                  (option, i) => (
                    <option key={`${option.value}_${i + 1}`} value={option.value}>
                      {option.text}
                    </option>
                  )
                )}
            </select>
          )}
          {errors.technology && <p className="text-red-500">{errors.technology.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="vendorId" className="text-text-hint mb-1">
            Vendor{!isJobSelected && <span className="text-danger-500"> *</span>}
          </label>
          {isJobSelected ? (
            <input
              type="text"
              value={selectedJobDetails.vendorName}
              readOnly
              className="outline-none border-2 border-secondary-500 rounded-lg bg-gray-100 cursor-not-allowed"
            />
          ) : (
            <select
              {...register('vendorId', { required: 'Vendor is required' })}
              className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
              disabled={isReadOnly}
            >
              {!refVendorLoading &&
                mapToOption(refVendor.response?.data, 'name', 'vendor_id').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
            </select>
          )}
          {errors.vendorId && <p className="text-red-500">{errors.vendorId.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="clientCompany" className="text-text-hint mb-1">
            Client Name{!isJobSelected && <span className="text-danger-500"> *</span>}
          </label>
          <input
            {...(isJobSelected ? {} : register('clientCompany', { required: 'Client Name is required' }))}
            value={isJobSelected ? selectedJobDetails.clientName : undefined}
            id="clientCompany"
            type="text"
            readOnly={isJobSelected || isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg ${isJobSelected || isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
          {errors.clientCompany && <p className="text-red-500">{errors.clientCompany.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="salesPersonId" className="text-text-hint mb-1">
            Sales Person{!isJobSelected && <span className="text-danger-500"> *</span>}
          </label>
          {isJobSelected ? (
            <input
              type="text"
              value={selectedJobDetails.salesPersonName}
              readOnly
              className="outline-none border-2 border-secondary-500 rounded-lg bg-gray-100 cursor-not-allowed"
            />
          ) : (
            <select
              {...register('salesPersonId', { required: 'Sales Person is required' })}
              className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
              disabled={isReadOnly}
            >
              <option value="">Select Sales Person</option>
              {!salesPersonsLoading &&
                salesPersons?.map((person) => (
                  <option key={person.manager_id} value={person.manager_id}>
                    {person.first_name} {person.last_name}
                  </option>
                ))}
            </select>
          )}
          {errors.salesPersonId && <p className="text-red-500">{errors.salesPersonId.message}</p>}
        </div>
      </div>

      {/* Date and Time */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex flex-col w-40">
          <label htmlFor="interviewDate" className="text-text-hint mb-1 text-sm">
            Interview Date <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('interviewDate', { required: 'Interview Date is required' })}
            id="interviewDate"
            type="date"
            disabled={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm ${disabledClass}`}
          />
          {errors.interviewDate && <p className="text-red-500 text-xs">{errors.interviewDate.message}</p>}
        </div>
        <div className="flex flex-col w-24">
          <label htmlFor="interviewTime" className="text-text-hint mb-1 text-sm">
            Time <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('interviewTime', {
              required: 'Time is required',
              pattern: { value: /^(0?[1-9]|1[0-2]):[0-5][0-9]$/, message: 'HH:MM' },
            })}
            id="interviewTime"
            type="text"
            placeholder="HH:MM"
            maxLength={5}
            onChange={handleTimeInputChange}
            disabled={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm ${disabledClass}`}
          />
          {errors.interviewTime && <p className="text-red-500 text-xs">{errors.interviewTime.message}</p>}
        </div>
        <div className="flex flex-col w-20">
          <label htmlFor="interviewTimeAmPm" className="text-text-hint mb-1 text-sm">
            AM/PM <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('interviewTimeAmPm', { required: 'Required' })}
            id="interviewTimeAmPm"
            disabled={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm ${disabledClass}`}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
          {errors.interviewTimeAmPm && (
            <p className="text-red-500 text-xs">{errors.interviewTimeAmPm.message}</p>
          )}
        </div>
        <div className="flex flex-col w-36">
          <label htmlFor="interviewTimezone" className="text-text-hint mb-1 text-sm">
            Timezone <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('interviewTimezone', { required: 'Timezone is required' })}
            id="interviewTimezone"
            disabled={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg px-2 py-1.5 text-sm ${disabledClass}`}
          >
            {timezoneOptions.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          {errors.interviewTimezone && (
            <p className="text-red-500 text-xs">{errors.interviewTimezone.message}</p>
          )}
        </div>
      </div>

      {/* Interviewers */}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="interviewer1" className="text-text-hint mb-1">
            Interviewer 1 <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('interviewer1', { required: 'Interviewer 1 is required' })}
            id="interviewer1"
            type="text"
            readOnly={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
          />
          {errors.interviewer1 && <p className="text-red-500">{errors.interviewer1.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="interviewer2" className="text-text-hint mb-1">
            Interviewer 2
          </label>
          <input
            {...register('interviewer2')}
            id="interviewer2"
            type="text"
            readOnly={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
          />
        </div>
      </div>

      {/* Links */}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col w-72">
          <label htmlFor="appointmentLink" className="text-text-hint mb-1">
            Appointment Link <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('appointmentLink', { required: 'Appointment Link is required' })}
            id="appointmentLink"
            type="text"
            readOnly={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
          />
          {errors.appointmentLink && <p className="text-red-500">{errors.appointmentLink.message}</p>}
        </div>
        <div className="flex flex-col w-72">
          <label htmlFor="appointmentDetails" className="text-text-hint mb-1">
            Appointment Details <span className="text-danger-500">*</span>
          </label>
          <input
            {...register('appointmentDetails', { required: 'Appointment Details is required' })}
            id="appointmentDetails"
            type="text"
            readOnly={isReadOnly}
            className={`outline-none border-2 border-secondary-500 rounded-lg ${disabledClass}`}
          />
          {errors.appointmentDetails && (
            <p className="text-red-500">{errors.appointmentDetails.message}</p>
          )}
        </div>
      </div>

      {/* Interview Status */}
      <div>
        <div className="w-full max-w-xl">
          <label className="text-text-hint" htmlFor="schedulingStatus">
            Interview Status <span className="text-danger-500">*</span>
          </label>
          <ul className="grid grid-cols-4 mt-1">
            {['In Progress', 'Shortlisted', 'Not Selected', 'Selected'].map((status) => (
              <li className="relative" key={status}>
                <input
                  {...register('schedulingStatus', { required: 'Interview status is required' })}
                  type="radio"
                  value={status}
                  id={status}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <label
                  className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 focus:outline-none ${
                    isReadOnly ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:bg-gray-50'
                  } ${
                    schedulingStatus === status ? '!bg-secondary-500 text-white border-secondary-800' : ''
                  } ${
                    status === 'In Progress'
                      ? 'rounded-tl-full rounded-bl-full'
                      : status === 'Selected'
                      ? 'rounded-tr-full rounded-br-full'
                      : ''
                  }`}
                  htmlFor={status}
                >
                  {status}
                </label>
              </li>
            ))}
          </ul>
          {errors.schedulingStatus && (
            <span className="text-danger-500">{errors.schedulingStatus.message}</span>
          )}
        </div>
      </div>

      {/* Rating */}
      <div>
        <h3>Rating</h3>
        <StarRating initialRating={parseInt(watch('rating')) || 0} fontSize={24} onRatingChange={isReadOnly ? () => {} : onRatingChange} />
      </div>

      {/* Feedback */}
      <div className="grid grid-cols-2 gap-5">
        <div className="flex flex-col col-span-3">
          <label htmlFor="feedback" className="text-text-hint mb-1">
            Feedback
          </label>
          <textarea
            {...register('feedback')}
            id="feedback"
            placeholder="feedback"
            readOnly={isReadOnly}
            className={`focus:outline-none border rounded-lg ${disabledClass}`}
            rows={4}
          />
        </div>
      </div>

      {/* Display-only audit fields for Edit Mode */}
      {!!watch('interviewId') && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Created By</label>
            <p className="text-sm text-gray-600">{watch('created_by') || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Date Created</label>
            <p className="text-sm text-gray-600">
              {watch('created') ? formatDateCST(watch('created')) : 'N/A'}
            </p>
            {watch('created') && (
              <p className="text-xs text-gray-500">{formatTimeCST(watch('created'))}</p>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Updated By</label>
            <p className="text-sm text-gray-600">{watch('updated_by') || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Last Updated</label>
            <p className="text-sm text-gray-600">
              {watch('updated') ? formatDateCST(watch('updated')) : 'N/A'}
            </p>
            {watch('updated') && (
              <p className="text-xs text-gray-500">{formatTimeCST(watch('updated'))}</p>
            )}
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className={`flex ${isReadOnly ? 'justify-end' : 'justify-between'} p-6 mt-6`}>
        <button
          type="button"
          onClick={closeModal}
          disabled={isSubmitting}
          className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Close
        </button>
        {!isReadOnly && (
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`py-2 px-5 rounded-full transition ${isValid && !isSubmitting ? 'bg-secondary-700 text-text-light hover:bg-secondary-800 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>
    </form>
  );
};

export default CandidateInterviewsForm;
